import {
  Alert,
  Box,
  Container,
  ContentLayout,
  Flashbar,
  Header,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import PLCButton from 'features/plc/components/PLCButton';
import { normalizePressId } from 'features/plc/contracts/machines';
import {
  PLC_PATHS,
  PRESS_MODE,
} from 'features/plc/contracts/pressHMI.schema';
import { useHMIHandles } from 'features/plc/providers/HMIHandleProvider';
import {
  usePLC,
  usePLCError,
  usePLCReady,
  usePLCValue,
  usePressTitle,
} from 'features/plc/providers/PLCProvider';
import React, { useEffect } from 'react';

import BondingContent from './bonding';
import HeatAlerts from './components/heatAlerts';
import FormingContent from './forming';
import ToolSwapContent from './toolSwap';
import TuningContent from './tuning';

function HMIRouter() {
  const ready = usePLCReady();
  const connectionError = usePLCError();
  const pressTitle = usePressTitle();
  const { writeError, pressId } = usePLC();
  const { setGroups } = useHMIHandles();
  const pressMode = usePLCValue(PLC_PATHS.cmd.pressMode);
  const machinePressId = usePLCValue(PLC_PATHS.meta.pressId);
  const normalizedMachinePressId = normalizePressId(machinePressId);
  const hasMismatch = Boolean(
    pressId && normalizedMachinePressId && pressId !== normalizedMachinePressId,
  );

  useEffect(() => {
    if (pressMode === PRESS_MODE.FORMING) {
      setGroups(['base', 'common', 'forming']);
      return;
    }

    if (pressMode === PRESS_MODE.BONDING) {
      setGroups(['base', 'common', 'bonding']);
      return;
    }

    if (pressMode === PRESS_MODE.TUNING) {
      setGroups(['base', 'common', 'tuning']);
      return;
    }

    if (pressMode === PRESS_MODE.TOOL_SWAP) {
      setGroups(['base', 'common', 'toolSwap']);
      return;
    }

    setGroups(['base']);
  }, [pressMode, setGroups]);

  if (!ready && !connectionError) {
    return (
      <Box textAlign="center" padding="xl">
        <Spinner size="large" />
      </Box>
    );
  }

  if (connectionError) {
    return (
      <ContentLayout
        header={(
          <Header variant="h1">
            {pressTitle}
          </Header>
        )}
      >
        <Alert type="error" header="PLC connection failed">
          {connectionError}
        </Alert>
      </ContentLayout>
    );
  }

  if (hasMismatch) {
    return (
      <ContentLayout
        header={(
          <Header variant="h1">
            {pressTitle}
          </Header>
        )}
      >
        <Alert type="error" header="Press identity mismatch">
          The URL requested
          {' '}
          <strong>{pressId}</strong>
          , but the connected machine reported
          {' '}
          <strong>{machinePressId}</strong>
          . Stop here and verify the routing or PLC endpoint before using this HMI.
        </Alert>
      </ContentLayout>
    );
  }

  return (
    <SpaceBetween size="l">
      {writeError ? (
        <Flashbar
          items={[{
            id: 'plc_write_error',
            type: 'error',
            header: 'PLC write failed',
            content: writeError,
            dismissible: false,
          }]}
        />
      ) : null}

      <HeatAlerts />

      {pressMode === PRESS_MODE.FORMING ? (
        <FormingContent />
      ) : null}

      {pressMode === PRESS_MODE.BONDING ? (
        <BondingContent />
      ) : null}

      {pressMode === PRESS_MODE.TUNING ? (
        <TuningContent />
      ) : null}

      {pressMode === PRESS_MODE.TOOL_SWAP ? (
        <ToolSwapContent />
      ) : null}

      {pressMode === PRESS_MODE.NONE || pressMode === undefined ? (
        <ContentLayout
          header={(
            <Header variant="h1">
              {pressTitle}
            </Header>
          )}
        >
          <Container>
            <SpaceBetween size="l">
              <div>Select the mode of operation for this press.</div>
              <PLCButton
                path={PLC_PATHS.cmd.pressMode}
                value={PRESS_MODE.FORMING}
              >
                Forming Mode
              </PLCButton>
              <PLCButton
                path={PLC_PATHS.cmd.pressMode}
                value={PRESS_MODE.BONDING}
              >
                Bonding Mode
              </PLCButton>
              <PLCButton
                path={PLC_PATHS.cmd.pressMode}
                value={PRESS_MODE.TUNING}
              >
                Tuning Mode
              </PLCButton>
              <PLCButton
                path={PLC_PATHS.cmd.pressMode}
                value={PRESS_MODE.TOOL_SWAP}
              >
                Cool Down Mode
              </PLCButton>
            </SpaceBetween>
          </Container>
        </ContentLayout>
      ) : null}
    </SpaceBetween>
  );
}

export default HMIRouter;
